import React, { useCallback, useRef, useState } from "react";
import ReactFlow, {
  Background, Controls, MiniMap, addEdge,
  useNodesState, useEdgesState, MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import { api } from "@/lib/api";
// Relative: these resolve for real under Jest, where the "@" alias does not exist.
import { automationErrorMessage, refusedActionField, refusedRuleName, ruleNameMessage } from "./automation/automationErrors";
import { actionLabel, configIssueText, triggerLabel } from "./automation/automationCodes";
import { configForSave, invalidConfigFields, ruleNameProblem } from "./automation/actionConfigRules";
import ActionConfigForm from "./automation/ActionConfigForm";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, Zap, GitBranch, X, Trash2 } from "lucide-react";

const NODE_STYLE = {
  trigger: { background: "#E6B85C20", border: "1px solid #E6B85C", color: "#fff" },
  action: { background: "#7FB8A420", border: "1px solid #7FB8A4", color: "#fff" },
};
const EDGE = { animated: true, style: { stroke: "#E6B85C" }, markerEnd: { type: MarkerType.ArrowClosed, color: "#E6B85C" } };
const TRIGGER_NODE = "trigger";
const DEFAULT_TRIGGER = "tenant.signup";

/**
 * One action of the rule as the editor holds it, keyed by its canvas node id:
 *   { id, type, config, stored, edited, unreadable }
 * `stored` is the action exactly as the server sent it; an action the operator did not edit goes
 * back as `stored`, untouched (its config, and any key this editor does not know). An action
 * stored before validation existed that is not an object is `unreadable` (type "invalid"): it is
 * shown, cannot be edited, and must be removed before the actions can be saved.
 */
const itemFromStored = (action, index) => {
  const id = `action-${index}`;
  if (!action || typeof action !== "object" || Array.isArray(action)) {
    return { id, type: "invalid", config: {}, stored: action, edited: false, unreadable: true };
  }
  const config = action.config && typeof action.config === "object" && !Array.isArray(action.config) ? action.config : {};
  return { id, type: action.type, config, stored: action, edited: false, unreadable: false };
};
// The rule's stored actions as the runner and `config_issues` count them: `null` is none, a lone
// value is one action at index 0.
const storedActions = (rule) => (Array.isArray(rule?.actions) ? rule.actions : rule?.actions == null ? [] : [rule.actions]);
// What `config_issues[].field` names when it is not a config field the form can show.
const STRUCTURAL_FIELDS = ["config", "type"];

/**
 * The fields to flag when the editor opens, per action id, from the rule's `config_issues`
 * (hq-customers-contract.md 4.4): the field the server reports plus every other field of
 * that action the form's own check refuses (the server names only the first), so one visit
 * shows all the work.
 */
const flagsFromIssues = (rule, items) => {
  const flags = {};
  (Array.isArray(rule?.config_issues) ? rule.config_issues : []).forEach((issue) => {
    const item = items[issue.index];
    if (!item) return;
    const local = item.unreadable ? [] : invalidConfigFields(item.type, item.config);
    flags[item.id] = [...new Set([...(flags[item.id] || []), issue.field, ...local])];
  });
  return flags;
};

const toPayload = (item) => (item.edited
  ? { ...(item.stored && typeof item.stored === "object" ? item.stored : {}), type: item.type, config: configForSave(item.type, item.config) }
  : item.stored);

/**
 * The rule editor on a canvas. `triggers` / `actions` are the backend catalogue (GET
 * /super-admin/hq/automation/catalogue), passed down by CustomRulesPanel (BUG-013).
 *
 * It round-trips the rule: the PUT is partial, and the editor sends only what the operator
 * changed here (name, trigger, actions). `enabled` and `conditions` are not edited on this
 * screen and never sent, so saving cannot re-enable a disabled rule or clear its conditions.
 * Actions are sent only when one was added, removed or edited, and then every action the
 * operator did not touch goes back exactly as it was stored. An action carries the config the
 * server requires (hq-customers-contract.md 4.3), collected in the side panel. Opened on a rule
 * that needs setup (`config_issues`), it selects the first action to fix with its fields flagged.
 */
export default function VisualWorkflowEditor({ rule, triggers = [], actions: actionTypes = [], onClose, onSaved }) {
  const t = useT("superadmin");
  const [items, setItems] = useState(() => storedActions(rule).map(itemFromStored));
  const [actionsEdited, setActionsEdited] = useState(false);
  const [trigger, setTrigger] = useState(rule?.trigger || DEFAULT_TRIGGER);
  const [ruleName, setRuleName] = useState(rule ? rule.name : t("vwe.newRule"));
  // Why the name is refused (`ruleNameProblem`'s shape, or the server's `invalid_rule_name`), null when it is not.
  const [nameProblem, setNameProblem] = useState(null);
  // Fields to flag, per action id: `{ [id]: [field, ...] }`, from the rule's config_issues, the
  // form's own check or a 400.
  const [flagged, setFlagged] = useState(() => flagsFromIssues(rule, items));
  const [selected, setSelected] = useState(() => Object.keys(flagged)[0] || null);
  const [saving, setSaving] = useState(false);
  const nextId = useRef(items.length);

  const [nodes, setNodes, onNodesChange] = useNodesState([
    { id: TRIGGER_NODE, type: "input", position: { x: 50, y: 100 }, data: {}, style: NODE_STYLE.trigger },
    ...items.map((item, i) => ({ id: item.id, position: { x: 350 + (i * 30), y: 100 + (i * 80) }, data: {}, style: NODE_STYLE.action })),
  ]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(items.map((item, i) => ({
    id: `e-${item.id}`, source: i === 0 ? TRIGGER_NODE : items[i - 1].id, target: item.id, ...EDGE,
  })));
  const onConnect = useCallback((params) => setEdges((eds) => addEdge({ ...params, ...EDGE }, eds)), [setEdges]);

  // Node words come from the model on every render, so they follow the language and the trigger.
  const byId = Object.fromEntries(items.map((item) => [item.id, item]));
  const shownNodes = nodes.map((n) => ({
    ...n,
    data: { label: n.id === TRIGGER_NODE ? `⚡ ${triggerLabel(t, trigger)}` : `🔧 ${actionLabel(t, byId[n.id]?.type)}` },
  }));
  // A stored trigger the catalogue no longer offers stays selectable, under the generic label.
  const triggerOptions = triggers.includes(trigger) ? triggers : [trigger, ...triggers];

  const editActions = (update) => { setItems(update); setActionsEdited(true); };
  const addAction = (type) => {
    const id = `action-${nextId.current++}`;
    editActions((cur) => [...cur, { id, type, config: {}, stored: null, edited: true, unreadable: false }]);
    setNodes((nds) => [...nds, { id, position: { x: 200 + (nds.length * 40), y: 200 + (nds.length * 60) }, data: {}, style: NODE_STYLE.action }]);
    setSelected(id); // a new action needs its settings: open them
  };
  const removeAction = (id) => {
    editActions((cur) => cur.filter((item) => item.id !== id));
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
    setFlagged((cur) => { const next = { ...cur }; delete next[id]; return next; });
    if (selected === id) setSelected(null);
  };
  const setConfigField = (id, name, value) => {
    editActions((cur) => cur.map((item) => (item.id === id ? { ...item, config: { ...item.config, [name]: value }, edited: true } : item)));
    setFlagged((cur) => ({ ...cur, [id]: (cur[id] || []).filter((f) => f !== name) }));
  };

  const changes = rule
    ? {
      ...(ruleName !== rule.name ? { name: ruleName } : {}),
      ...(trigger !== rule.trigger ? { trigger } : {}),
      ...(actionsEdited ? { actions: items.map(toPayload) } : {}),
    }
    : { name: ruleName, trigger, actions: items.map(toPayload) };
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!dirty || saving) return;
    // The name is checked only when it is sent: a stored name the operator did not touch is
    // not part of a partial update.
    const badName = "name" in changes ? ruleNameProblem(changes.name) : null;
    setNameProblem(badName);
    if (badName) {
      toast.error(t("hauto.cfg.fixFields"));
      return;
    }
    if ("actions" in changes) {
      const problems = Object.fromEntries(items
        .map((item) => [item.id, item.unreadable ? ["config"] : invalidConfigFields(item.type, item.config)])
        .filter(([, fields]) => fields.length > 0));
      if (Object.keys(problems).length > 0) {
        setFlagged(problems);
        setSelected(Object.keys(problems)[0]);
        toast.error(t("hauto.cfg.fixFields"));
        return;
      }
    }
    setSaving(true);
    try {
      if (rule?.rule_id) await api.put(`/super-admin/hq/automation/rules/${rule.rule_id}`, changes);
      else await api.post("/super-admin/hq/automation/rules", changes);
      toast.success(t("vwe.saved"));
      onSaved && onSaved();
    } catch (e) {
      const refused = refusedActionField(e);
      const item = refused && "actions" in changes ? items[refused.index] : null;
      if (item) {
        setFlagged({ [item.id]: [refused.field] });
        setSelected(item.id);
      }
      if (refusedRuleName(e)) setNameProblem({ code: "invalid" });
      toast.error(automationErrorMessage(e, t));
    } finally {
      setSaving(false);
    }
  };

  const current = selected ? byId[selected] : null;
  // A problem of the selected action the form has no field for. A stored config that is not an
  // object reads as empty, and filling the fields replaces it; a type the runner has no handler
  // for can only be removed. An unreadable action has its own sentence below.
  const structuralField = current ? (flagged[current.id] || []).find((f) => STRUCTURAL_FIELDS.includes(f)) : null;
  const structuralIssue = structuralField && !current.unreadable && !(structuralField === "config" && current.edited)
    ? structuralField : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="m-4 w-full bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col" data-testid="visual-editor">
        <div className="border-b border-slate-100 p-4 flex items-center gap-3">
          <div className="max-w-sm w-full">
            <Input value={ruleName} onChange={(e) => { setRuleName(e.target.value); setNameProblem(null); }}
              aria-required="true" aria-invalid={Boolean(nameProblem)} aria-describedby={nameProblem ? "vwe-rule-name-error" : undefined}
              className={`bg-white ${nameProblem ? "border-rose-400" : "border-slate-200"}`} placeholder={t("vwe.phRuleName")} aria-label={t("vwe.phRuleName")} data-testid="rule-name-input" />
            {nameProblem && (
              <p id="vwe-rule-name-error" role="alert" className="text-xs text-rose-600 mt-1" data-testid="rule-name-input-error">{ruleNameMessage(nameProblem, t)}</p>
            )}
          </div>
          <select value={trigger} onChange={(e) => setTrigger(e.target.value)} aria-label={t("vwe.legendTrigger")}
            className="bg-white border border-slate-200 rounded-md px-3 py-2 text-sm" data-testid="trigger-select">
            {triggerOptions.map((tr) => <option key={tr} value={tr} className="bg-white">{triggerLabel(t, tr)}</option>)}
          </select>
          <div className="flex gap-1 flex-wrap">
            {actionTypes.map((a) => (
              <button key={a} type="button" onClick={() => addAction(a)} data-testid={`add-action-${a}`}
                className="text-xs px-2 py-1 rounded-full border border-sol-sage/30 text-sol-sage hover:bg-sol-sage/10">
                + {actionLabel(t, a)}
              </button>
            ))}
          </div>
          <div className="flex-1" />
          <Button onClick={save} disabled={!dirty || saving} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="save-workflow">
            <Save className="w-4 h-4 mr-2" /> {t("vwe.save")}
          </Button>
          <button type="button" onClick={onClose} aria-label={t("hc.close")}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 flex min-h-0">
          <div className="flex-1 bg-[#0a0a14]">
            <ReactFlow
              nodes={shownNodes} edges={edges}
              onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
              onNodesDelete={(deleted) => deleted.filter((n) => n.id !== TRIGGER_NODE).forEach((n) => removeAction(n.id))}
              onNodeClick={(_e, node) => setSelected(node.id === TRIGGER_NODE ? null : node.id)}
              onConnect={onConnect} fitView
            >
              <Background color="#ffffff10" gap={20} />
              <Controls className="!bg-slate-50 !border-slate-200" />
              <MiniMap className="!bg-slate-50" maskColor="#0f0f1c80" nodeColor="#E6B85C" />
            </ReactFlow>
          </div>
          <aside className="w-80 shrink-0 border-l border-slate-100 p-4 overflow-y-auto" data-testid="action-settings">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-3">{t("hauto.cfg.title")}</div>
            {current ? (
              <div className="space-y-3">
                <div className="font-medium text-sm text-slate-800">{actionLabel(t, current.type)}</div>
                {structuralIssue && (
                  <p className="text-xs text-rose-600" role="alert" data-testid="cfg-issue">{configIssueText(t, structuralIssue)}</p>
                )}
                {current.unreadable ? (
                  <p className="text-xs text-rose-600" role="alert">{t("vwe.unreadableAction")}</p>
                ) : (
                  <ActionConfigForm type={current.type} config={current.config} invalid={flagged[current.id] || []}
                    onChange={(name, value) => setConfigField(current.id, name, value)} />
                )}
                <Button type="button" variant="ghost" onClick={() => removeAction(current.id)} className="text-rose-500 hover:bg-rose-50" data-testid="remove-action">
                  <Trash2 className="w-4 h-4 mr-2" /> {t("vwe.removeAction")}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-slate-400">{t("vwe.actionSettingsHint")}</p>
            )}
          </aside>
        </div>
        <div className="border-t border-slate-100 p-3 text-xs text-slate-400 flex items-center gap-2" data-testid="vwe-legend">
          <Zap className="w-3 h-3 text-amber-600" /> {t("vwe.legendTrigger")}
          <GitBranch className="w-3 h-3 text-sol-sage" /> {t("vwe.legendActions")}
          <span className="ml-auto">{t("vwe.dragHint")}</span>
        </div>
      </div>
    </div>
  );
}
